import { useState, useEffect } from 'react';
import Layout from '../../components/dashboard/Layout';
import CreateProjectModal from '../../components/projects/CreateProjectModal';
import ManageProjectModal from '../../components/projects/ManageProjectModal';
import { useNavigate } from 'react-router-dom';

interface Project {
  id: string;
  name: string;
  whitelisted_users: number;
  created_at: string;
}

export default function Projects() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Fetch projects when component mounts
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects');
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    }
  };

  const handleCreateProject = async (projectData: { name: string }) => {
    try {
      setError(''); // Clear any existing errors
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to create project');
      }

      setProjects([...projects, data]);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error instanceof Error ? error.message : 'Failed to create project');
    }
  };

  const handleDelete = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    try {
      setError(''); // Clear any existing errors
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to delete project');
      }

      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete project');
    }
  };

  const handleManage = (project: Project) => {
    setSelectedProject(project);
    setIsManageModalOpen(true);
  };

  const handleCopyLoader = async (projectId: string) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/loader`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.message);

      // Copy to clipboard using the browser API since we're not in the executor
      await navigator.clipboard.writeText(data.loader);
      
      // Show a temporary success message
      const tempDiv = document.createElement('div');
      tempDiv.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg';
      tempDiv.textContent = 'Loader script copied to clipboard!';
      document.body.appendChild(tempDiv);
      
      setTimeout(() => {
        document.body.removeChild(tempDiv);
      }, 2000);
    } catch (err) {
      console.error('Error copying loader:', err);
      setError(err instanceof Error ? err.message : 'Failed to copy loader');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <span>+</span>
            Create new project
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-gray-800 rounded-lg p-6">
          {projects.length === 0 ? (
            <div className="text-gray-400 text-center py-8">
              No projects yet. Click "Create new project" to get started.
            </div>
          ) : (
            <div className="grid gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-gray-700 rounded-lg p-4"
                >
                  <div className="flex justify-between items-center">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/dashboard/project/${project.id}`)}
                    >
                      <h3 className="text-white font-semibold">{project.name}</h3>
                      <p className="text-gray-400 text-sm">
                        {project.whitelisted_users} whitelisted users
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLoader(project.id);
                        }}
                        className="text-white bg-indigo-600 px-3 py-1 rounded hover:bg-indigo-700 flex items-center gap-2"
                      >
                        <span>📋</span>
                        Copy Script
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleManage(project);
                        }}
                        className="text-white bg-blue-600 px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Manage
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(project.id);
                        }}
                        className="text-white bg-red-600 px-3 py-1 rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateProject}
      />

      {selectedProject && (
        <ManageProjectModal
          isOpen={isManageModalOpen}
          onClose={() => {
            setIsManageModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}
    </Layout>
  );
} 